import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DateInputFillMode, DateInputRounded, DateInputSize } from '@progress/kendo-angular-dateinputs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { EMAIL_VALIDATOR_REGEX } from 'src/app/extensions/constants/form-validator.constants';
import { RequestEvents } from 'src/app/extensions/services/api/events';
import { RequestAPIService } from 'src/app/extensions/services/api/request-api.service';
import { TenantAPIService } from 'src/app/extensions/services/api/tenant-api.service';
import { UploadAPIService } from 'src/app/extensions/services/api/upload-api.service';

@Component({
  selector: 'app-add-delegate',
  templateUrl: './add-delegate.component.html',
  styleUrls: ['./add-delegate.component.scss'],
  standalone: false,
})
export class AddDelegateComponent implements OnInit {
  public fillModeOutline: DateInputFillMode = 'outline';
  public roundedLarge: DateInputRounded = 'large';
  public sizeLarge: DateInputSize = 'large';
  @ViewChild('addDelegate') public addDelegateform: NgForm;

  @Input() newDelegate: app.models.Delegate = {} as app.models.Delegate;
  @Input() isModal: boolean = false;
  @Output() backToList: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() delegateSaved: EventEmitter<app.models.Delegate> = new EventEmitter<app.models.Delegate>();

  localeValidators: app.models.Validator[] = {} as app.models.Validator[];
  loading: boolean = false;

  phoneValidator: app.models.Validator;
  pinValidator: app.models.Validator;
  emailValidator = EMAIL_VALIDATOR_REGEX;
  public today: Date = new Date();

  submitButtonText: string;

  selectedFile: File | null = null;
  originalDelegate: app.models.Delegate;

  constructor(
    private _notificationService: NotificationService,
    private _translate: TranslateService,
    private _tenantAPIService: TenantAPIService,
    private _requestAPIService: RequestAPIService,
    private _uploadAPIService: UploadAPIService
  ) {}

  ngOnInit(): void {
    this.getLocaleValidators();
    if (this.newDelegate.delegateId != null) {
      this.originalDelegate = { ...this.newDelegate };
      this.submitButtonText = 'requests.wizard.addDelegateForm.editDelegateBtn';
    } else {
      this.submitButtonText = 'requests.wizard.addDelegateForm.addDelegateBtn';
    }
  }

  getLocaleValidators() {
    this._tenantAPIService.getLocaleValidators().subscribe(result => {
      this.localeValidators = result;

      this.phoneValidator = this.localeValidators.find(x => x.code == 'MOBILE_PHONE');
      this.pinValidator = this.localeValidators.find(x => x.code == 'PIN');
    });
  }

  handlerSaveDelegate() {
    if (!this.addDelegateform.valid) {
      return;
    }

    this.loading = true;
    this._requestAPIService.saveDelegate(this.newDelegate).subscribe(
      result => {
        if (result.isSuccess) {
          this._notificationService.showSuccess(
            this._translate.instant('requests.wizard.addDelegateForm.delegateSaved'),
            this._translate.instant('common.success')
          );
          this.newDelegate.delegateId = result.response.events.find(
            x => x.eventType == RequestEvents.DelegateSaved
          ).eventBody.delegateId;
        } else if (!result.hasValidationErrors) {
          this._notificationService.showError(
            this._translate.instant('requests.wizard.addDelegateForm.delegateSaveFailed'),
            this._translate.instant('common.error')
          );
        }

        this.loading = false;
        this.delegateSaved.emit(this.newDelegate);
      },
      () => {
        this.loading = false;
      }
    );
  }

  public disabledDates = (date: Date): boolean => {
    const yesterday = (d => new Date(d.setDate(d.getDate() - 1)))(new Date());
    return date.getTime() < yesterday.getTime();
  };

  backToDelegatesList() {
    this.addDelegateform.reset(this.originalDelegate);
    this.backToList.emit(true);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    this.selectedFile = input.files[0];
    const formData: FormData = new FormData();
    formData.append('file', this.selectedFile, this.selectedFile.name);

    this._uploadAPIService.extractIdentificationInfo(formData).subscribe(response => {
      if (!response) return;

      if (response) {
        this.newDelegate.firstName = response.name;
        this.newDelegate.lastName = response.surname;
        this.newDelegate.personalIdNumber = response.personalIdentificationNumber;
        this.newDelegate.idSerialNumber = `${response.documentSeries ?? ''} ${response.documentNumber ?? ''}`.trim();
        this.newDelegate.expiryDate = response.documentExpiryDate ? new Date(response.documentExpiryDate) : null;
        this.newDelegate.issuedBy = response.documentIssuedBy;
      }
    });
  }

  removeFile(event: Event) {
    event.stopPropagation();
    this.selectedFile = null;
    const fileInput = document.querySelector<HTMLInputElement>('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  }
}
